import * as React from "react";
import { Provider } from "react-redux";
import { ThemeProvider } from "styled-components";
import { GoogleOAuthProvider } from "@react-oauth/google";

import featureToggles from "@assets/feature-toggles/feature-toggles.app.json";

import { GOOGLE_LOGIN_CLIENT_ID } from "@config/consts";

import DefaultTheme from "@ui/themes/default";
import { ErrorBoundary } from "@ui/Atoms";
import { ErrorFallback } from "@ui/Molecules";

import { DialogManager } from "@features/DialogManager";
import { DrawerManager } from "@features/DrawerManager";

import { resolveCountryFromUrl } from "@services/ResolveCountryFromUrl";

import { AuthProvider } from "@hooks/useAuth";
import { FeatureToggleProvider } from "@hooks/useFeatureToggle";
import { ConfigProvider } from "@hooks/useConfig";
import { BreakpointProvider } from "@hooks/useBreakpoint";
import { useTranslate } from "@hooks/useTranslate";
import { ModalProvider } from "@hooks/modal/ModalProvider";
import { PrismicProvider } from "@hooks/usePrismic";
import { CountOfNotificationsProvider } from "@hooks/useNumberOfNotifications";
import { TelemetryProvider } from "@hooks/useTelemetry";
import { SurveyProvider } from "@hooks/useSurvey";
import { UTMProvider } from "@hooks/useUTM";

import store from "@app/redux/store";
import { CookieAccessGuard } from "@app/containers/CookieAccessGuard";
import { LanguageDetector } from "@app/containers/LanguageDetector";
import { UserNotificationsProvider } from "@app/hooks/useUserNotifications";

interface Props {
  children: React.ReactNode;
}

const AppProviders: React.FC<Props> = (props): React.ReactElement => {
  const translate = useTranslate();

  return (
    <ThemeProvider theme={DefaultTheme}>
      <ErrorBoundary
        fallbackUi={() => (
          <ErrorFallback
            somethingWentWrongText={translate(
              "batmaid_pwa_generic.something_went_wrong",
            )}
            appCrashExplanationText={translate(
              "batmaid_pwa_generic.app_crash_explanation",
            )}
          />
        )}
      >
        <Provider store={store}>
          <PrismicProvider>
            <GoogleOAuthProvider clientId={String(GOOGLE_LOGIN_CLIENT_ID)}>
              <CookieAccessGuard>
                <ModalProvider>
                  <DrawerManager.Provider>
                    <DialogManager.Provider>
                      <BreakpointProvider>
                        <ConfigProvider country={resolveCountryFromUrl()}>
                          <LanguageDetector>
                            <TelemetryProvider>
                              <AuthProvider application="client">
                                <UTMProvider>
                                  <FeatureToggleProvider
                                    builtConfig={featureToggles ?? undefined}
                                  >
                                    <CountOfNotificationsProvider>
                                      <SurveyProvider>
                                        <UserNotificationsProvider>
                                          {props.children}
                                        </UserNotificationsProvider>
                                      </SurveyProvider>
                                    </CountOfNotificationsProvider>
                                  </FeatureToggleProvider>
                                </UTMProvider>
                              </AuthProvider>
                            </TelemetryProvider>
                          </LanguageDetector>
                        </ConfigProvider>
                      </BreakpointProvider>
                    </DialogManager.Provider>
                  </DrawerManager.Provider>
                </ModalProvider>
              </CookieAccessGuard>
            </GoogleOAuthProvider>
          </PrismicProvider>
        </Provider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default AppProviders;
